import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { PhysicsWorld } from '../physics/PhysicsWorld';
import { VehiclePhysics } from '../physics/VehiclePhysics';

export class PlayerBike {
  private vehiclePhysics: VehiclePhysics;
  private mesh: THREE.Group;
  private wheelMeshes: THREE.Mesh[] = [];

  // Visual lean effect
  private currentLean: number = 0;
  private targetLean: number = 0;
  private readonly MAX_LEAN_ANGLE = 0.4; // radians (~23 degrees)
  private readonly LEAN_SPEED = 8; // How fast to lean

  constructor(
    scene: THREE.Scene,
    physicsWorld: PhysicsWorld,
    position: THREE.Vector3
  ) {
    // Create physics
    this.vehiclePhysics = new VehiclePhysics(
      physicsWorld,
      new CANNON.Vec3(position.x, position.y, position.z)
    );

    // Create visual mesh
    this.mesh = this.createBikeMesh();
    this.mesh.position.copy(position);
    scene.add(this.mesh);

    // Create wheel visuals
    this.createWheelMeshes(scene);
  }

  private createBikeMesh(): THREE.Group {
    const group = new THREE.Group();

    // === MATERIALS ===
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0xd0d0d8, // Silver/white body
      metalness: 0.95,
      roughness: 0.1,
    });

    const darkMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a1a2e,
      metalness: 0.9,
      roughness: 0.2,
    });

    const accentMaterial = new THREE.MeshStandardMaterial({
      color: 0x00ffff,
      emissive: 0x00ffff,
      emissiveIntensity: 2,
    });

    // === MAIN BODY - Angular futuristic shape ===
    // Lower body section (frame)
    const frameShape = new THREE.Shape();
    frameShape.moveTo(-0.15, 0);
    frameShape.lineTo(0.15, 0);
    frameShape.lineTo(0.12, 0.25);
    frameShape.lineTo(-0.12, 0.25);
    frameShape.closePath();

    const frameExtrudeSettings = { depth: 1.8, bevelEnabled: false };
    const frameGeom = new THREE.ExtrudeGeometry(frameShape, frameExtrudeSettings);
    const frame = new THREE.Mesh(frameGeom, darkMaterial);
    frame.position.set(0, 0.15, -0.9);
    group.add(frame);

    // Upper body/fairing - sleek angular shape
    const fairingShape = new THREE.Shape();
    fairingShape.moveTo(0, 0);
    fairingShape.lineTo(0.18, 0.08);
    fairingShape.lineTo(0.15, 0.35);
    fairingShape.lineTo(0.08, 0.45);
    fairingShape.lineTo(-0.08, 0.45);
    fairingShape.lineTo(-0.15, 0.35);
    fairingShape.lineTo(-0.18, 0.08);
    fairingShape.closePath();

    const fairingExtrudeSettings = { depth: 1.2, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.02 };
    const fairingGeom = new THREE.ExtrudeGeometry(fairingShape, fairingExtrudeSettings);
    const fairing = new THREE.Mesh(fairingGeom, bodyMaterial);
    fairing.position.set(0, 0.25, -0.3);
    group.add(fairing);

    // Sharp front nose cone
    const noseGeom = new THREE.ConeGeometry(0.15, 0.8, 4);
    const nose = new THREE.Mesh(noseGeom, bodyMaterial);
    nose.rotation.x = Math.PI / 2;
    nose.rotation.y = Math.PI / 4;
    nose.position.set(0, 0.45, 1.1);
    group.add(nose);

    // Angular tail fin/spoiler rising up
    const finShape = new THREE.Shape();
    finShape.moveTo(0, 0);
    finShape.lineTo(0.03, 0);
    finShape.lineTo(0.03, 0.5);
    finShape.lineTo(-0.15, 0.3);
    finShape.lineTo(-0.15, 0.1);
    finShape.closePath();

    const finExtrudeSettings = { depth: 0.02, bevelEnabled: false };
    const finGeom = new THREE.ExtrudeGeometry(finShape, finExtrudeSettings);
    const tailFin = new THREE.Mesh(finGeom, darkMaterial);
    tailFin.position.set(-0.01, 0.5, -0.85);
    group.add(tailFin);

    // Red tail light strip on fin
    const tailLightGeom = new THREE.BoxGeometry(0.04, 0.02, 0.25);
    const tailLightMat = new THREE.MeshStandardMaterial({
      color: 0xff0040,
      emissive: 0xff0040,
      emissiveIntensity: 3,
    });
    const tailLight = new THREE.Mesh(tailLightGeom, tailLightMat);
    tailLight.position.set(0, 0.65, -0.75);
    tailLight.rotation.x = -0.3;
    group.add(tailLight);

    // === SUSPENSION - Green coil springs ===
    const springMaterial = new THREE.MeshStandardMaterial({
      color: 0x00ff44,
      emissive: 0x00ff44,
      emissiveIntensity: 1.5,
    });

    // Front suspension coil
    for (let i = 0; i < 6; i++) {
      const coilGeom = new THREE.TorusGeometry(0.06, 0.015, 8, 16);
      const coil = new THREE.Mesh(coilGeom, springMaterial);
      coil.position.set(0.12, 0.15 + i * 0.04, 0.5);
      coil.rotation.y = Math.PI / 2;
      group.add(coil);
    }

    // Rear suspension coil
    for (let i = 0; i < 5; i++) {
      const coilGeom = new THREE.TorusGeometry(0.05, 0.012, 8, 16);
      const coil = new THREE.Mesh(coilGeom, springMaterial);
      coil.position.set(0.1, 0.18 + i * 0.035, -0.55);
      coil.rotation.y = Math.PI / 2;
      group.add(coil);
    }

    // === ACCENTS AND DETAILS ===
    // Side accent stripe (cyan)
    const stripeGeom = new THREE.BoxGeometry(0.01, 0.08, 0.8);
    const leftStripe = new THREE.Mesh(stripeGeom, accentMaterial);
    leftStripe.position.set(-0.17, 0.45, 0.2);
    group.add(leftStripe);

    const rightStripe = new THREE.Mesh(stripeGeom, accentMaterial.clone());
    rightStripe.position.set(0.17, 0.45, 0.2);
    group.add(rightStripe);

    // Headlight cluster
    const headlightGeom = new THREE.CircleGeometry(0.06, 16);
    const headlightMat = new THREE.MeshStandardMaterial({
      color: 0x00ffff,
      emissive: 0x00ffff,
      emissiveIntensity: 4,
    });
    const headlight = new THREE.Mesh(headlightGeom, headlightMat);
    headlight.position.set(0, 0.45, 1.45);
    group.add(headlight);

    // Dashboard/display screen
    const screenGeom = new THREE.PlaneGeometry(0.15, 0.08);
    const screenMat = new THREE.MeshStandardMaterial({
      color: 0x0088ff,
      emissive: 0x0044ff,
      emissiveIntensity: 2,
      transparent: true,
      opacity: 0.9,
    });
    const screen = new THREE.Mesh(screenGeom, screenMat);
    screen.position.set(0, 0.55, 0.5);
    screen.rotation.x = -0.5;
    group.add(screen);

    // Engine glow underneath
    const engineGlowGeom = new THREE.BoxGeometry(0.2, 0.02, 0.6);
    const engineGlowMat = new THREE.MeshStandardMaterial({
      color: 0x8000ff,
      emissive: 0x8000ff,
      emissiveIntensity: 1.5,
    });
    const engineGlow = new THREE.Mesh(engineGlowGeom, engineGlowMat);
    engineGlow.position.set(0, 0.12, -0.1);
    group.add(engineGlow);

    return group;
  }

  private createWheelMeshes(scene: THREE.Scene): void {
    // Hubless wheel design - outer tire ring only
    const tireOuterRadius = 0.38;
    const tireInnerRadius = 0.28;
    const tireWidth = 0.18;

    const tireMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      metalness: 0.3,
      roughness: 0.8,
    });

    const rimMaterial = new THREE.MeshStandardMaterial({
      color: 0x2a2a3e,
      metalness: 0.9,
      roughness: 0.2,
    });

    // Glowing hub lights (purple like in image)
    const hubGlowMaterial = new THREE.MeshStandardMaterial({
      color: 0x8844ff,
      emissive: 0x6622dd,
      emissiveIntensity: 2,
    });

    for (let i = 0; i < 2; i++) {
      const wheelGroup = new THREE.Group();

      // Outer tire (torus for hubless look)
      const tireGeom = new THREE.TorusGeometry(
        (tireOuterRadius + tireInnerRadius) / 2,
        (tireOuterRadius - tireInnerRadius) / 2,
        16,
        32
      );
      const tire = new THREE.Mesh(tireGeom, tireMaterial);
      tire.rotation.y = Math.PI / 2;
      wheelGroup.add(tire);

      // Inner rim ring
      const innerRimGeom = new THREE.TorusGeometry(tireInnerRadius, 0.025, 8, 32);
      const innerRim = new THREE.Mesh(innerRimGeom, rimMaterial);
      innerRim.rotation.y = Math.PI / 2;
      wheelGroup.add(innerRim);

      // Outer rim ring
      const outerRimGeom = new THREE.TorusGeometry(tireOuterRadius, 0.015, 8, 32);
      const outerRim = new THREE.Mesh(outerRimGeom, rimMaterial);
      outerRim.rotation.y = Math.PI / 2;
      wheelGroup.add(outerRim);

      // Glowing hub accent lights (small spheres around inner rim)
      for (let j = 0; j < 4; j++) {
        const angle = (j / 4) * Math.PI * 2;
        const hubLight = new THREE.Mesh(
          new THREE.SphereGeometry(0.025, 8, 8),
          hubGlowMaterial
        );
        hubLight.position.set(
          Math.cos(angle) * tireInnerRadius * 0.7,
          Math.sin(angle) * tireInnerRadius * 0.7,
          0
        );
        wheelGroup.add(hubLight);
      }

      // Side glow ring
      const sideGlowGeom = new THREE.TorusGeometry(tireInnerRadius + 0.02, 0.008, 8, 32);
      const sideGlowMat = new THREE.MeshStandardMaterial({
        color: 0x00ffff,
        emissive: 0x00ffff,
        emissiveIntensity: 1.5,
      });

      const leftGlow = new THREE.Mesh(sideGlowGeom, sideGlowMat);
      leftGlow.position.x = -tireWidth / 2 - 0.01;
      leftGlow.rotation.y = Math.PI / 2;
      wheelGroup.add(leftGlow);

      const rightGlow = new THREE.Mesh(sideGlowGeom, sideGlowMat.clone());
      rightGlow.position.x = tireWidth / 2 + 0.01;
      rightGlow.rotation.y = Math.PI / 2;
      wheelGroup.add(rightGlow);

      scene.add(wheelGroup);
      this.wheelMeshes.push(wheelGroup as unknown as THREE.Mesh);
    }
  }

  public setInput(engineForce: number, steerValue: number, brake: boolean): void {
    this.vehiclePhysics.setEngineForce(engineForce);
    this.vehiclePhysics.setSteerValue(steerValue);
    this.vehiclePhysics.setBrake(brake);

    // Calculate target lean based on steering and speed
    // Lean INTO the turn (left turn = lean left = negative rotation)
    const speed = Math.abs(this.vehiclePhysics.getSpeed());
    const speedFactor = Math.min(speed / 20, 1); // Full lean at 20+ m/s
    this.targetLean = -steerValue * this.MAX_LEAN_ANGLE * speedFactor;
  }

  public update(dt: number, groundHeight: number = 0, onRamp: boolean = false): void {
    // Update physics with arcade tuning
    this.vehiclePhysics.update(dt, groundHeight, onRamp);

    // Smooth lean interpolation
    const leanDiff = this.targetLean - this.currentLean;
    this.currentLean += leanDiff * Math.min(this.LEAN_SPEED * dt, 1);

    // Sync visual mesh with physics
    const pos = this.vehiclePhysics.getPosition();
    const quat = this.vehiclePhysics.getQuaternion();

    this.mesh.position.set(pos.x, pos.y, pos.z);

    // Apply physics rotation first
    this.mesh.quaternion.set(quat.x, quat.y, quat.z, quat.w);

    // Apply visual lean around the bike's local forward (Z) axis
    // Get the bike's forward direction
    const forward = new THREE.Vector3(0, 0, 1);
    forward.applyQuaternion(this.mesh.quaternion);

    // Create lean rotation around the forward axis
    const leanQuat = new THREE.Quaternion();
    leanQuat.setFromAxisAngle(forward, this.currentLean);

    // Apply lean (premultiply to rotate in world space around forward axis)
    this.mesh.quaternion.premultiply(leanQuat);

    // Update wheel positions (manually positioned relative to bike)
    const wheelOffsets = [
      new THREE.Vector3(0, 0, 0.8),  // Front wheel
      new THREE.Vector3(0, 0, -0.8), // Rear wheel
    ];

    const speed = this.vehiclePhysics.getSpeed();
    const wheelRotationSpeed = speed * 3; // Visual wheel spin

    for (let i = 0; i < this.wheelMeshes.length; i++) {
      // Position wheel relative to bike
      const offset = wheelOffsets[i].clone();
      offset.applyQuaternion(this.mesh.quaternion);

      this.wheelMeshes[i].position.copy(this.mesh.position).add(offset);
      this.wheelMeshes[i].quaternion.copy(this.mesh.quaternion);

      // Spin wheel based on speed
      this.wheelMeshes[i].rotateX(wheelRotationSpeed * dt);
    }
  }

  public getPosition(): THREE.Vector3 {
    const pos = this.vehiclePhysics.getPosition();
    return new THREE.Vector3(pos.x, pos.y, pos.z);
  }

  public getRotation(): THREE.Quaternion {
    const quat = this.vehiclePhysics.getQuaternion();
    return new THREE.Quaternion(quat.x, quat.y, quat.z, quat.w);
  }

  public getSpeed(): number {
    return this.vehiclePhysics.getSpeed();
  }

  public getSpeedKmh(): number {
    return this.vehiclePhysics.getSpeedKmh();
  }

  public getMesh(): THREE.Group {
    return this.mesh;
  }

  public getForwardVector(): THREE.Vector3 {
    const fwd = this.vehiclePhysics.getForwardVector();
    return new THREE.Vector3(fwd.x, fwd.y, fwd.z);
  }

  public isInAir(): boolean {
    return this.vehiclePhysics.isInAir();
  }

  public getAirTime(): number {
    return this.vehiclePhysics.getAirTime();
  }
}
